import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { useCreateTemporaryDealerAccessRequest } from "hooks/useTemporaryDealerAccess";
import { normalizeLifeguardSerial } from "utils/lifeGuardSerial";
import { notify } from "utils/notify";

interface TemporaryAccessRequestDialogProps {
  visible: boolean;
  onHide: () => void;
  requestLimitReached?: boolean;
}

export const TemporaryAccessRequestDialog: React.FC<
  TemporaryAccessRequestDialogProps
> = ({ visible, onHide, requestLimitReached = false }) => {
  const [serialNumber, setSerialNumber] = useState("");
  const createRequest = useCreateTemporaryDealerAccessRequest();

  useEffect(() => {
    if (!visible) setSerialNumber("");
  }, [visible]);

  const submit = async () => {
    if (requestLimitReached) {
      notify.warning("You can have at most 10 pending or active temporary access requests.");
      return;
    }

    const normalizedSerial = normalizeLifeguardSerial(serialNumber);
    if (!normalizedSerial) {
      notify.warning("Enter a Lifeguard serial number.");
      return;
    }

    try {
      await createRequest.mutateAsync(normalizedSerial);
      notify.success("Temporary-access request sent. It is awaiting customer approval.");
      onHide();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        "This Lifeguard is not eligible for temporary access.";
      notify.error(message);
    }
  };

  const disabled = createRequest.isPending || requestLimitReached;

  return (
    <Dialog
      header="Request temporary access"
      visible={visible}
      onHide={onHide}
      style={{ width: "min(32rem, 92vw)" }}
      modal
      draggable={false}
    >
      {requestLimitReached ? (
        <p style={{ lineHeight: 1.5 }}>
          You already have the maximum of 10 pending or active temporary access
          requests. Cancel a pending request or wait for an active request to
          finish before creating another one.
        </p>
      ) : (
        <>
          <p style={{ lineHeight: 1.5 }}>
            Enter the Lifeguard serial number. The customer will receive an email
            asking them to approve diagnostic access for 72 hours.
          </p>
          <label
            htmlFor="temporary-access-serial"
            style={{ display: "block", fontWeight: 600, marginBottom: 8 }}
          >
            Lifeguard serial number
          </label>
          <InputText
            id="temporary-access-serial"
            value={serialNumber}
            onChange={(event) => setSerialNumber(event.target.value)}
            placeholder="e.g. DWLG-12345678"
            disabled={disabled}
            style={{ width: "100%" }}
            autoFocus
          />
          <p style={{ fontSize: "0.875rem", color: "#5f6368", marginTop: 12 }}>
            Customer contact details are never shown to dealers. Access expires automatically and cannot be extended.
          </p>
        </>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
        <Button
          type="button"
          label={requestLimitReached ? "Close" : "Cancel"}
          severity="secondary"
          outlined
          onClick={onHide}
          disabled={createRequest.isPending}
        />
        {!requestLimitReached && (
          <Button
            type="button"
            label={createRequest.isPending ? "Sending…" : "Send request"}
            icon="pi pi-send"
            onClick={submit}
            disabled={disabled}
          />
        )}
      </div>
    </Dialog>
  );
};
